import { useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { fmt } from "@/lib/format";
import { marginColorClass } from "@/lib/thresholds";
import { totalExpenseKrw, netProfitKrw } from "@/lib/timeseries";
import RowDelta from "@/components/RowDelta";

// 핵심 3개 지표(매출/지출/순이익)만 남기고 그 외는 뺐다(2026-07-17 피드백) — 크레딧 충전/
// 크레딧 사용(NC 단위)·활성 사용자·토큰·AI원가(USD)·매출총이익은 "매출·지출·순이익이
// 핵심 아니냐"는 지적에 전부 정리. 지출은 AI원가+관리자 입력 운영비를 합친 값 하나로
// (그 세부 내역은 아래 CostMatrix 의 항목×날짜 매트릭스에서 본다). 컬럼이 적어져 예전의
// 2단 그룹 헤더(매출·비용/사용자/원가/손익)도 더는 필요 없어 단일 헤더로 단순화했다.
// 마진%/캐시히트%는 "AI 사용 효율"을 보는 참고 지표라 손익과는 별개로 흐리게 유지.
const COLUMNS = [
  { key: "revenueKrw", label: "매출", render: (r) => fmt.krw(r.revenueKrw), className: "font-semibold" },
  { key: "expense", label: "지출", render: (r) => fmt.krw(totalExpenseKrw(r)), className: "font-semibold" },
  {
    key: "netProfit",
    label: "직접비 기여이익",
    render: (r) => fmt.krw(netProfitKrw(r)),
    className: (r) => `font-semibold ${netProfitKrw(r) >= 0 ? "text-emerald-600" : "text-red-600"}`,
  },
  { key: "marginPct", label: "마진%", render: (r) => fmt.pct(r.marginPct), className: (r) => marginColorClass(r.marginPct) },
  { key: "cacheHitRate", label: "캐시히트%", render: (r) => fmt.pct(r.cacheHitRate), className: "text-ink-400" },
];

// 기간별 매출/지출/순이익 흐름을 하나의 표로 (일/주/월 재집계된 series 사용).
// cumulative=true(누적 모드)면 상단 합계 행 생략 — 누적치를 또 더하면 이중 집계라 무의미.
export default function TrendTable({ series, cumulative = false }) {
  const [dir, setDir] = useState("desc"); // "desc"(최신순, 기본) | "asc"(과거순)
  const rows = series || [];
  if (rows.length === 0) {
    return <div className="rounded-lg border border-ink-200 bg-white p-6 text-center text-sm text-ink-400">데이터 없음</div>;
  }
  const displayRows = dir === "desc" ? rows : [...rows].reverse();

  // 상단 합계 행(2026-07-18 피드백 — 스크롤 끝까지 안 내려가도 총량부터). 마진%/캐시히트%
  // 는 비율이라 단순 합산이 성립하지 않아 "—".
  const totalRevenue = rows.reduce((s, r) => s + (r.revenueKrw || 0), 0);
  const totalExpense = rows.reduce((s, r) => s + totalExpenseKrw(r), 0);

  // 직전 구간 대비 순이익 증감(2026-07-18) — rows 는 최신순이라 다음 인덱스가 직전 구간.
  const prevProfitByBucket = new Map();
  rows.forEach((r, i) => {
    if (i + 1 < rows.length) prevProfitByBucket.set(r.bucket, netProfitKrw(rows[i + 1]));
  });

  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-ink-200">
      {/* table-fixed 균등 열(2026-07-18) — 사용량 표와 같은 원칙: 열 폭이 내용에 끌려다니지
          않게 고정 분할, 셀 간격 일관성 유지. */}
      <table className="w-full table-fixed divide-y divide-ink-200 text-sm" style={{ minWidth: "34rem" }}>
        <thead className="bg-ink-50">
          <tr>
            <DateTh dir={dir} onToggle={() => setDir((d) => (d === "desc" ? "asc" : "desc"))} />
            {COLUMNS.map((col) => (
              <th key={col.key} className="px-3 py-2 text-right text-xs font-medium text-ink-500">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100 bg-white">
          {!cumulative && (
            <tr className="border-b-2 border-ink-200 bg-ink-50 font-semibold">
              <td className="px-3 py-2 text-ink-900">합계</td>
              <td className="px-3 py-2 text-right tabular-nums text-ink-900">{fmt.krw(totalRevenue)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-ink-900">{fmt.krw(totalExpense)}</td>
              <td className={`px-3 py-2 text-right tabular-nums ${totalRevenue - totalExpense >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {fmt.krw(totalRevenue - totalExpense)}
              </td>
              <td className="px-3 py-2 text-right text-ink-300">—</td>
              <td className="px-3 py-2 text-right text-ink-300">—</td>
            </tr>
          )}
          {displayRows.map((r) => (
            <tr key={r.bucket} className="hover:bg-ink-50">
              <Td className="whitespace-nowrap font-medium text-ink-900">{r.bucketLabel || r.bucket}</Td>
              {COLUMNS.map((col) => {
                const cls = typeof col.className === "function" ? col.className(r) : (col.className || "");
                return (
                  <Td key={col.key} align="right" className={`tabular-nums ${cls}`}>
                    {col.render(r)}
                    {col.key === "netProfit" && (
                      <RowDelta
                        cur={netProfitKrw(r)}
                        prev={prevProfitByBucket.has(r.bucket) ? prevProfitByBucket.get(r.bucket) : null}
                        hidden={cumulative}
                        format={(v) => fmt.krw(v)}
                      />
                    )}
                  </Td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {/* 비율 열은 헤더만으론 뜻이 안 와닿는다는 피드백 대응 — 손익 금액(절댓값)과 달리
          "무엇을 무엇으로 나눈 값(상댓값)"인지 항상 보이게 표 밑에 한 줄로 풀어 준다.
          숨는 ⓘ(InfoTooltip) 대신 상시 각주로 둔 이유: 이 표는 overflow-x-auto 안이라
          팝업이 잘릴 수 있고, "몰라서 못 본" 지표라 발견성이 높아야 한다. */}
      <p className="mt-2 text-[12px] leading-relaxed text-ink-400">
        <b className="font-semibold text-ink-500">마진%</b> = 순이익 ÷ 매출 (이 기간 매출 중 남은 비율).{" "}
        <b className="font-semibold text-ink-500">캐시히트%</b> = AI가 반복되는 입력을 캐시에서 싸게 처리한 비율(캐시 읽기 ÷ 입력·읽기·쓰기 토큰) — 높을수록 원가↓, 손익과는 무관한 참고 지표.
      </p>
    </>
  );
}

// 시간순 표는 임의 컬럼 정렬이 아니라 "최신순/과거순" 방향 토글만 의미가 있다 — 전체 사용자
// 탭의 클릭 정렬과 같은 시각 언어(화살표 아이콘)를 써서 일관성을 맞춘다(2026-07-16).
function DateTh({ dir, onToggle }) {
  const Icon = dir === "desc" ? ChevronDown : ChevronUp;
  return (
    <th className="w-24 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
      <button type="button" onClick={onToggle} className="inline-flex items-center gap-1 hover:text-ink-800">
        기간
        <Icon className="h-3.5 w-3.5" />
      </button>
    </th>
  );
}

function Td({ children, align = "left", className = "" }) {
  return <td className={`px-3 py-2 text-${align} ${className}`}>{children}</td>;
}
